// src/modals/AboutPanel.jsx
// Phase 37 Plan 06 — extracted from src/App.jsx (was lines 5236-5351 pre-Plan-37-06).
//
// Scope: UI-03 — about panel showing build info, data version, plant count,
// privacy/terms links, dev mode activation Easter egg, and Ko-fi support button.
//
// Note: `dataVersion` is now passed in as a prop (was the module-private
// DATA_VERSION constant) so this component can live independently of App.jsx.

import { useRef, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { logAffiliateClick, FEATURE_SOURCES } from '../data/affiliates.js';
import { CURRENT_MONTH } from '../constants.js';

const DEV_BLUE = "#1976d2";

export default function AboutPanel({plants,serverKeyReady,devModeAllowed,devModeActive,onActivateDevMode,tier,onShowLegal,dataVersion,onClose,commercialFeaturesEnabled}) {
  const KOFI_URL = 'https://ko-fi.com/tend_envision8';
  const tapCount = useRef(0);
  const tapTimer = useRef(null);

  // Cleanup tap state when modal unmounts (close resets the count)
  useEffect(()=>()=>{ tapCount.current=0; clearTimeout(tapTimer.current); },[]);

  function handleBuildTap() {
    if (!devModeAllowed || devModeActive) return;
    tapCount.current++;
    clearTimeout(tapTimer.current);
    tapTimer.current = setTimeout(()=>{ tapCount.current=0; }, 5000);
    if (tapCount.current >= 10) {
      tapCount.current=0; clearTimeout(tapTimer.current);
      onActivateDevMode();
    }
  }

  return(
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:16}} onClick={onClose}>
      <div style={{background:"var(--c-bg)",borderRadius:18,padding:28,maxWidth:400,width:"100%",boxShadow:"0 8px 40px rgba(0,0,0,0.2)"}} onClick={e=>e.stopPropagation()}>
        <h2 style={{fontFamily:'var(--font-family-display)',color:"var(--c-heading)",margin:"0 0 4px",fontSize:"var(--font-h1)",fontWeight:700}}>Tend</h2>
        <p style={{fontFamily:'var(--font-family-body)',fontSize:'var(--font-body)',color:"var(--c-text-2)",margin:"0 0 18px",fontStyle:"italic"}}>Your personalised garden companion</p>
        <hr style={{border:"none",borderTop:"1px solid var(--c-border)",margin:"0 0 18px"}}/>
        <div style={{display:"flex",flexDirection:"column",gap:10,fontSize:'var(--font-body)',color:"var(--c-text)"}}>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <span className="t-muted">Author</span>
            <span style={{fontWeight:600}}>Mark Towers</span>
          </div>
          {/* Build row — hidden tap target for developer mode activation */}
          <div style={{display:"flex",justifyContent:"space-between",userSelect:"none"}} onClick={handleBuildTap}>
            <span className="t-muted">Build</span>
            <span style={{fontWeight:600,fontFamily:"monospace",fontSize:'var(--font-small)'}}>{__GIT_HASH__} · {__GIT_DATE__}</span>
          </div>
          {devModeActive&&(
            <div style={{display:"flex",justifyContent:"flex-end"}}>
              <span style={{fontWeight:700,fontSize:'var(--font-small)',color:DEV_BLUE}}>Developer Mode — Active</span>
            </div>
          )}
          {tier==="test"&&(
            <div style={{display:"flex",justifyContent:"flex-end"}}>
              <span style={{fontWeight:700,fontSize:'var(--font-small)',color:DEV_BLUE}}>Beta Tester</span>
            </div>
          )}
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <span className="t-muted">Data version</span>
            <span style={{fontWeight:600}}>{dataVersion}</span>
          </div>
          <div style={{display:"flex",justifyContent:"space-between"}}>
            <span className="t-muted">Plants</span>
            <span style={{fontWeight:600}}>{plants.length}</span>
          </div>
          {devModeActive&&(
          <div className="row-between">
            <span className="t-muted">Source</span>
            <a href="https://github.com/Mark-Towers/garden-planner" target="_blank" rel="noreferrer" style={{color:"var(--c-heading)",fontWeight:600,textDecoration:"none"}}>GitHub ↗</a>
          </div>
          )}
          <div className="row-between">
            <span className="t-muted">Privacy Policy</span>
            <button
              onClick={() => onShowLegal('privacy')}
              style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-heading)",fontWeight:600,textDecoration:"none",padding:0,font:"inherit"}}
            >View ›</button>
          </div>
          <div className="row-between">
            <span className="t-muted">Terms of Service</span>
            <button
              onClick={() => onShowLegal('terms')}
              style={{background:"none",border:"none",cursor:"pointer",color:"var(--c-heading)",fontWeight:600,textDecoration:"none",padding:0,font:"inherit"}}
            >View ›</button>
          </div>
          {devModeActive&&(
          <div className="row-between">
            <span className="t-muted">API Connection</span>
            {serverKeyReady
              ? <span style={{background:"var(--c-heading)",color:"#fff",borderRadius:20,padding:"4px 12px",fontSize:'var(--font-small)',fontWeight:600}}>Connected</span>
              : <span style={{background:"var(--c-border)",color:"var(--c-text-3)",borderRadius:20,padding:"4px 12px",fontSize:'var(--font-small)',fontWeight:600}}>Not connected</span>
            }
          </div>
          )}
        </div>
        {commercialFeaturesEnabled && (<>
        <hr style={{border:"none",borderTop:"1px solid var(--c-border)",margin:"16px 0 0"}}/>
        <a
          href={KOFI_URL}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => {
            e.preventDefault();
            logAffiliateClick({
              linkId: 'kofi-support',
              feature: FEATURE_SOURCES.SUPPORT,
              month: CURRENT_MONTH,
            });
            window.open(KOFI_URL, '_system');
          }}
          style={{
            display:"flex", alignItems:"center", justifyContent:"center",
            gap:8, marginTop:16, padding:"12px 0",
            background:"var(--c-brand-deep)", color:"#fff",
            borderRadius:20, textDecoration:"none",
            fontWeight:600, fontSize:'var(--font-body)', width:"100%",
          }}
        >
          <Heart size={15} /> Support Our Work
        </a>
        </>)}
        <button onClick={onClose} style={{marginTop:12,background:"var(--c-border)",color:"var(--c-text-2)",border:"none",borderRadius:20,padding:"9px 24px",fontSize:'var(--font-body)',fontWeight:600,width:"100%"}}>Close</button>
      </div>
    </div>
  );
}
